/* Northsheep — the phone gallery as one filmstrip (owner, 2026-09-16).

   WHAT IT IS FOR
   The gallery box used to change shape with every photo: a wide room shot made
   it short and wide, an upright hide made it tall and narrow, and the page
   jumped between the two. On phones the answer is one strip — every photo the
   same height, each keeping its own shape, so a wide photo simply becomes a
   wide picture you keep swiping through. Nothing is cropped, nothing is
   padded.

   The sizing is all CSS (northsheep-product.css, .ns-gallery-strip). This file
   does the two things CSS cannot: say which photo you are looking at, and run
   the scrub bar.

   WHY DAWN CANNOT COUNT HERE
   Dawn works out the current slide by dividing the scroll position by a slide
   PITCH — the gap between the first two slides (global.js, initPages/update).
   That assumes every slide is the same width, which is exactly what this
   layout gives up. With mixed widths the counter drifts and the wrong
   thumbnail lights up.

   So the count follows the photo under the MIDDLE of the screen. An upright
   photo is narrower than the screen, so two can be visible at once and the
   middle one is the one being looked at; a wide photo spans several screens,
   so the middle stays inside it for the whole pan and the number holds until
   the next photo takes over.

   WHY IT DOES NOT PATCH DAWN
   Dawn binds its scroll listener in the constructor, so replacing the method
   afterwards changes nothing. Instead this listens to the same scroll event
   and runs AFTER it, writing the right numbers over the wrong ones in the same
   frame — nothing is ever painted wrong. The thumbnail follows the same way:
   media-gallery.js debounces its slideChanged handler by 500ms, so the event
   dispatched here, last, is the one that lands.

   A HANDLE OVER THE THUMBNAILS WAS TRIED AND REMOVED (2026-09-16). Four
   shapes — a proportional window, a window framing the current thumbnail, a
   pill on its centre, a playhead that tracked freely — and the owner did not
   like any of them: "can you undo all im not happy with how it turned out".
   The bar below is the design that replaced them, with a target big enough to
   actually hit. Do not re-introduce the overlay without being asked.

   Revert: theme editor -> product section -> uncheck "Phone gallery as one
   filmstrip". The class disappears with it and Dawn's own gallery returns.
*/
(function () {
  'use strict';

  /* THE STRIP IS NO LONGER PHONE-ONLY (owner, 2026-09-16: "can we now build
     the same concept for desktop/tablet etc").

     Everything here used to be gated on a phone media query. The layout is now
     a filmstrip at every width — phones take their height from the first
     photo, wider screens from the "Gallery height" setting — so the gate
     becomes "is this list actually a strip", which is true exactly when the
     Liquid put the class on it. That also means the setting switching the
     feature off takes the script with it, at every width.

     Kept as a function rather than inlined: the event handlers below run on
     the gallery element, where the list is one lookup away. */
  function isStrip(el) {
    if (!el) return false;
    if (el.classList && el.classList.contains('ns-gallery-strip')) return true;
    return !!(el.querySelector && el.querySelector('.ns-gallery-strip'));
  }

  function strips() {
    return Array.prototype.slice.call(document.querySelectorAll('.ns-gallery-strip'));
  }

  /* Rendered slides only. A variant change can leave a hidden one behind, and
     counting it would number the photos wrongly. */
  function slidesOf(list) {
    return Array.prototype.filter.call(list.children, function (el) {
      return el.offsetWidth > 0;
    });
  }

  /* A SAFETY NET, NOT THE MECHANISM.

     The photos are fetched with the page now — product-media-gallery.liquid
     renders every one of them without `loading="lazy"`, on the owner's
     instruction (21 Sep 2026: "cant you just directly load all product images
     on page visit"), because five of six were otherwise still unloaded when
     the first flick arrived and each one decoded on the main thread as the
     flick reached it, which is what a fling does not survive.

     This stays for the galleries that markup does not reach: one rebuilt by a
     variant change, or a section rendered through the Section Rendering API.
     On a normal page it finds nothing to do.

     See decodeAhead below — fetching is only half the work. */
  function wakeImages(list) {
    if (!list || list.dataset.nsWoke === '1') return;
    list.dataset.nsWoke = '1';
    var imgs = list.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].decoding = 'async';
      imgs[i].loading = 'eager';
    }
  }

  /* After the page has finished loading, so the gallery never competes with
     the first paint. requestIdleCallback where it exists (not on iOS Safari,
     which is the device this is for), a timer everywhere else. */
  /* FETCHED IS NOT THE SAME AS READY (owner, 21 Sep 2026: "the photos still
     dont load all untill i see the edge of the image ... all pictures out of
     frame are not loading till they come a tiny bit in frame").

     The photos now arrive with the page — measured: all six complete, none
     lazy. What was still missing is the second half of the work. A downloaded
     image is compressed bytes; it has to be DECODED to a bitmap before it can
     be painted, and by default that happens at paint time, on the main thread,
     the instant the photo is about to appear. That is why a photo still popped
     in as its edge came into frame although it had been in memory for a
     minute, and it is the same main-thread seizure that ends a fling.

     decode() does that work up front and off the main thread, and the browser
     keeps the result. By the time the strip is flicked every photo is a ready
     bitmap, so scrolling into one costs nothing.

     Failures are ignored on purpose: decode() rejects if the image is replaced
     or removed (a variant swap rebuilds the gallery), and that is not an error
     worth reporting — the photo simply decodes the old way if it is ever
     shown. */
  function decodeAhead(list) {
    var imgs = list.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img.decode || img.dataset.nsDecoded === '1') continue;
      img.dataset.nsDecoded = '1';
      img.decode().catch(function () {});
    }
  }

  function warmStrips() {
    strips().forEach(function (list) {
      wakeImages(list);
      decodeAhead(list);
    });
  }

  function scheduleWarm() {
    var go = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(warmStrips, { timeout: 1500 });
      else setTimeout(warmStrips, 300);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  function currentIndex(list, slides) {
    var mid = list.scrollLeft + list.clientWidth / 2;
    var best = 0;
    var bestGap = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var left = slides[i].offsetLeft;
      var right = left + slides[i].offsetWidth;
      if (mid >= left && mid < right) return i;
      var gap = mid < left ? left - mid : mid - right;
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }
    return best;
  }

  /* THE BAR IS NOT THIS FILE'S ANY MORE (owner, 2026-09-16: "use the same
     slider bar design that you use in the reviews ... so that we use the same
     styling across the webshop").

     The gallery now declares itself a `[data-ns-srow]` host and ns-scroll-row.js
     — the same component behind the related-products row and the reviews row —
     sizes the thumb, follows the scroll and handles drag-and-jump. Two
     implementations binding pointerdown on one element would have fought over
     every drag, so the bespoke one is gone rather than disabled.

     What stays here is the part that component cannot know: which PHOTO is on
     screen, for the counter and the thumbnail. */
  function sync(list) {
    if (!isStrip(list)) return;
    var comp = list.closest('slider-component');
    if (!comp) return;
    var slides = slidesOf(list);
    if (!slides.length) return;


    var i = currentIndex(list, slides);
    var current = comp.querySelector('.slider-counter--current');
    var total = comp.querySelector('.slider-counter--total');
    /* NOTHING IS WRITTEN TO THE DOM WHILE THE STRIP IS BEING FLUNG UNLESS IT
       HAS ACTUALLY CHANGED.

       This used to set `data-ns-strip-at` ON THE SCROLLING ELEMENT, and assign
       the counter's textContent on every scroll event whether the number had
       changed or not. Both are DOM writes landing on, or immediately beside,
       the element WebKit is flinging — and on a phone a fling is owned by the
       scrolling thread, which gives it back to the main thread when the main
       thread touches what it is scrolling. Measured on the owner's iPhone, a
       hard flick died at FULL SPEED mid-strip 10-20ms after this ran.

       The index is a memo this file keeps for itself — no stylesheet has ever
       read the attribute, and nothing outside this file reads it either — so
       it becomes a plain property and leaves the document alone. The counter
       still updates, but only when the number it shows is actually different,
       which on a flick through six photos is five writes instead of one per
       frame. */
    if (list.__nsStripAt === i) return;
    list.__nsStripAt = i;

    if (current && current.textContent !== String(i + 1)) current.textContent = i + 1;
    /* Dawn's total is pages, not photos: items - slidesPerPage + 1. With
       narrow upright photos two fit on screen, so it reports one short. */
    if (total && total.textContent !== String(slides.length)) total.textContent = slides.length;

    /* LIGHT THE THUMBNAIL DIRECTLY, rather than by dispatching slideChanged.

       media-gallery.js debounces that handler by 500ms, so only the LAST event
       in a burst is ever acted on — and Dawn's slider dispatches one of its
       own from the same scroll, computed from a fixed slide pitch that does
       not exist on a strip of mixed widths. Whenever its event lands last, its
       wrong index wins and the wrong photo lights up (owner: "it highlights
       the wrong image from the image im actually looking at").

       Dispatching after it cannot win, because the debounce keeps one event
       and throws the rest away. So the thumbnail is set here, immediately, and
       guardGallery() drops Dawn's events on this gallery so they cannot undo
       it half a second later. setActiveThumbnail also scrolls the row to keep
       the current thumbnail in view, which is what we want anyway. */
    var gal = list.closest('media-gallery');
    var id = slides[i] && slides[i].dataset ? slides[i].dataset.mediaId : null;
    if (gal && id) {
      var thumb = gal.querySelector('.thumbnail-list [data-target="' + id + '"]');
      if (thumb) lightThumb(list, gal, thumb);
    }
  }

  /* THE THUMBNAIL ROW MUST NOT SCROLL WHILE A FLICK IS STILL RUNNING.

     Owner, 21 Sep 2026: "i want to do a hard flick to scroll through multiple
     images but then as soon as i lift my thumb it ends the scroll" — and,
     decisively, "if i tab to zoom one image then i can scroll all other images
     normal ... But if i dont zoom any images then the scroll gets interupted".

     Measured on the phone: the flick stops at FULL SPEED, mid-strip, with
     nothing writing the strip's own scroll position — a fling ABORTED rather
     than slowed. The last thing to happen before each death was this function,
     10-20ms earlier.

     The culprit is the last line of Dawn's setActiveThumbnail:

         if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
         this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });

     On WebKit a fling is owned by the scrolling thread, and a programmatic
     scroll of ANY scroller forces a commit that ends it — it does not have to
     be the scroller under the finger. So lighting up a thumbnail was reaching
     across and killing the gesture on the photos.

     That early return is also why it looked random rather than broken: cross
     into a photo whose thumbnail is already on screen and nothing scrolls and
     the flick runs out normally; cross into one that is off screen and the row
     moves and the flick dies. Same gesture, opposite outcome, depending only
     on where the thumbnail row happened to be sitting.

     So the two halves of setActiveThumbnail are separated. The highlight is
     Dawn's own markup contract (aria-current on the button, which is what its
     CSS styles) and is applied here immediately, because the shopper should
     see the right thumbnail lit the instant the photo comes into frame. The
     ROW'S SCROLL is the only part that has to wait, and it waits for the strip
     to be genuinely at rest — the same test the settle uses, for the same
     reason. Nothing is lost: by the time you have stopped, the correct
     thumbnail is both lit and brought into view.

     It is a better row, too. It used to jump about under the photos all
     through a flick; now it moves once, when you stop. */
  function lightThumb(list, gal, thumb) {
    var btns = gal.querySelectorAll('.thumbnail-list button[aria-current]');
    for (var b = 0; b < btns.length; b++) btns[b].removeAttribute('aria-current');
    var btn = thumb.querySelector('button');
    if (btn) btn.setAttribute('aria-current', 'true');

    /* A HOVERED THUMBNAIL IS ALREADY IN VIEW (audit E6, 2 Oct 2026): the row
       used to scroll 160-340 ms after a hover switch and slide another
       thumbnail under the cursor (ns-thumb-hover.js stamps the time). */
    if (Date.now() - (window.__nsThumbHoverAt || 0) < 1000) {
      list.__nsThumbWanted = null;
      if (list.__nsThumbTimer) clearTimeout(list.__nsThumbTimer);
      list.__nsThumbTimer = 0;
      return;
    }

    if (typeof gal.setActiveThumbnail !== 'function') return;
    list.__nsThumbWanted = thumb;
    if (list.__nsThumbTimer) clearTimeout(list.__nsThumbTimer);

    var bringIntoView = function () {
      list.__nsThumbTimer = 0;
      /* A finger down, or the strip still travelling, means the gesture is not
         over — look again rather than reaching in. */
      if (list.__nsTouching || Date.now() - (list.__nsScrollAt || 0) < 160) {
        list.__nsThumbTimer = setTimeout(bringIntoView, 160);
        return;
      }
      var want = list.__nsThumbWanted;
      list.__nsThumbWanted = null;
      if (want && want.isConnected !== false) gal.setActiveThumbnail(want);
    };
    list.__nsThumbTimer = setTimeout(bringIntoView, 160);
  }

  /* BRING A SLIDE TO THE MIDDLE OF THE FRAME.

     Dawn sends the chosen variant's photo to the LEFT EDGE of the scroller
     (media-gallery.js: `scrollTo({ left: activeMedia.offsetLeft })`). On a row
     where every photo is a different width that can leave it half out of view,
     and the owner wants the colour they just picked centred: "when selecting
     variant it should jump to variant inside center of the frame".

     Measured from bounding rects rather than offsetLeft. The desktop gallery
     column is `position: sticky`, which makes it the offsetParent — so
     offsetLeft is not reliably relative to the list that actually scrolls,
     which is the other half of "it just jumps x distance". */
  /* Call off the follow-up centring passes below. The moment the shopper
     touches the strip themselves, a pass scheduled a moment ago stops being
     helpful and becomes a hand pulling them back — "it turns grabby again and
     pulls me back a couple times. everytime again if i select a different
     colour". Their gesture wins. */
  function cancelCentre(list) {
    var timers = list.__nsCentreTimers;
    if (!timers) return;
    for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
    list.__nsCentreTimers = null;
  }

  function centreSlide(list, slide) {
    if (!list || !slide) return;
    /* A wheel glide still easing towards an older target would drag the photo
       straight back off centre. */
    if (typeof list.__nsStopGlide === 'function') list.__nsStopGlide();
    var max = list.scrollWidth - list.clientWidth;
    if (max <= 0) return;
    var lr = list.getBoundingClientRect();
    var sr = slide.getBoundingClientRect();
    var target = list.scrollLeft + (sr.left - lr.left) - (list.clientWidth - sr.width) / 2;
    if (target < 0) target = 0;
    if (target > max) target = max;
    var prev = list.style.scrollBehavior;
    list.style.scrollBehavior = 'auto';
    list.scrollLeft = target;
    list.style.scrollBehavior = prev;
    sync(list);
  }

  /* SETTLE — land a photo in frame once scrolling stops.

     Owner: "im also a bit annoyed about the fact that i cant scroll the other
     images perfectly in frame so not sure how we can manage this without
     jumping to the image."

     Free scrolling means stopping anywhere, so a photo almost never ends up
     square in the frame. Snapping would fix that and take the freedom with it
     — and CSS scroll-snap has twice been the thing that broke a carousel in
     this theme (see CLAUDE.md), because it drags back any scroll shorter than
     one item. This is the gentler contract:

       - only when you have STOPPED (150ms of no movement),
       - only if the photo is ALREADY nearly in place (within a third of the
         frame), so a deliberate stop between two photos is left alone,
       - never for a photo wider than the frame — the room shot is meant to be
         explored from the inside,
       - and it eases rather than jumps, using the same motion as the wheel. */
  function settle(list) {

    /* THE FLING MUST BE OVER — NOT NEARLY OVER, OVER.

       This is the one rule the whole of 21 Sep 2026 produced, and the settle
       is the feature it was learned on. A fling on WebKit belongs to the
       scrolling thread; the main thread writing a scroll position takes it
       back, and the throw dies mid-air. For most of that day the settle was
       doing exactly that, because it was choosing its moment by a timer and
       every timer eventually fires inside someone's gesture.

       It was switched off on touch outright to prove the point. Owner, once
       the flick was fluent again: "then can we now safely put back the helper?
       to softly put images inside the frame? without destroying the
       functionality again" — and the answer is yes, on one condition, which is
       that "stopped" is MEASURED rather than assumed.

       Scroll events are the measurement. While a fling runs the browser keeps
       firing them; when they stop, it has genuinely run out, and a write then
       cancels nothing because there is nothing left to cancel. 200ms is the
       same quiet the counter waits for, so the photo squaring up and the
       number catching up happen together rather than as two separate events.

       Which leaves the settle doing only what the owner asked for in the first
       place — "only if people stop scrolling just over the edge of the image".
       The catchment below is a tenth of the frame: stop just over an edge and
       it tidies, stop deliberately between two photos and it leaves you there. */
    if (Date.now() - (list.__nsScrollAt || 0) < 200) return;
    /* ON PHONES TOO (owner, 20 Sep 2026: "can you then do the same as on the
       desktop that if someone stops scroll near the edge it softly slides into
       place same functionality").

       It was held to 750px and up because the phone strip was gutter-width,
       so a photo never filled the frame and "in frame" meant little. Now that
       a 3:4 photo is exactly the width of the screen, stopping a few pixels
       off leaves a sliver of the next photo down one edge — which is the
       thing the settle was written to tidy. Every guard below applies
       unchanged: a photo wider than the screen is still left alone, and a
       deliberate stop between two photos is still respected. */
    /* A FINGER STILL ON THE GLASS IS NOT A STOP.

       The wheel guard below has an exact counterpart on a phone. If the
       browser carries the gesture itself (ns-gallery-swipe hands over when it
       sees the strip scrolling natively), scroll events arrive all through the
       drag — so a finger held still mid-swipe would look exactly like someone
       who has finished, and the strip would pull itself out from under the
       thumb. The touch flags are set beside the scroll listener below. */
    if (list.__nsTouching) return;
    var lastTouch = list.__nsTouchAt || 0;
    if (Date.now() - lastTouch < 120) return;
    /* STILL WHEELING? THEN THEY HAVE NOT STOPPED.

       A mouse wheel arrives in separate notches, and the gap between two of
       them is easily longer than a short idle timer. Settling in that gap
       reaches in mid-gesture and drags the strip back towards the nearest
       photo — which is what the owner felt on EVERY photo, not only after a
       colour change: "it turns grabby again and pulls me back a couple
       times". A notch within the last third of a second means the gesture is
       still running. */
    var last = list.__nsWheelAt || 0;
    if (Date.now() - last < 120) return;
    var slides = slidesOf(list);
    if (!slides.length) return;
    var view = list.clientWidth;
    var max = list.scrollWidth - list.clientWidth;
    if (max <= 1) return;

    var slide = slides[currentIndex(list, slides)];
    if (!slide) return;
    var lr = list.getBoundingClientRect();
    var sr = slide.getBoundingClientRect();
    /* Wider than the frame: there is no "in frame" for it. */
    if (sr.width > view + 4) return;

    var ideal = list.scrollLeft + (sr.left - lr.left) - (view - sr.width) / 2;
    if (ideal < 0) ideal = 0;
    if (ideal > max) ideal = max;

    var off = Math.abs(ideal - list.scrollLeft);
    if (off < 1) return;

    /* NEVER UNDO THE DIRECTION THEY WERE TRAVELLING.

       Landing on a page and nudging the strip leaves you a little way past
       photo 1 — inside the net, and the nearest tidy position is BACK where
       you started. The gallery then hands you your own scroll in reverse,
       which is the "pulls me back a bit before i can start scrolling". It is
       the same move that made it feel grabby on every other photo too.

       So the settle may only carry on the way you were already going. Going
       backwards is left to you. `__nsLastPos` is recorded on every sync, so
       the direction is simply the last movement of the strip. */
    /* IT SETTLES IN BOTH DIRECTIONS.

       For a while it refused to correct backwards, to stop a landing nudge
       being undone. That rule cost more than it saved: stop a little PAST
       where a photo frames up — which is half of all stops — and the tidy
       position is behind you, so nothing happened at all. Owner: "somehow not
       every border pulls or settles and im not sure why".

       The pull-back it was guarding against had two other causes, both since
       found and fixed: the settle's easing and the wheel's easing were running
       at the same time and fighting over the scroll position, and the settle
       waited 400ms before starting, so it arrived as an interruption rather
       than as part of stopping. With those gone, a backwards nudge of a few
       pixels reads as the photo squaring up, which is what it is — and any
       notch of the wheel cancels it outright, so it can never become a
       tug-of-war. */
    /* HOW NEAR IS "NEARLY IN FRAME" — a fifth of the frame (owner, 21 Sep
       2026: "the settle seems to work but can you make the window a bit
       larger").

       A third was tried and was too wide: almost any resting place got pulled
       somewhere else, which reads as the gallery arguing with you. A tenth was
       this morning's value, chosen while the settle was still fighting the
       browser's fling — it was narrowed to keep it out of the way, not because
       a tenth was right. Now that it only ever runs once the strip has come to
       a complete stop, that reason is gone, and a fifth is the width the
       feature was tuned to before any of today's trouble.

       Beyond it, where the shopper stopped IS the answer: stop deliberately
       between two photos to hold both in view and the gallery leaves you
       there. One number, this line, if it wants adjusting again. */
    if (off > view * 0.2) return;

    /* `true` asks for the flowing curve rather than the wheel's decay. */
    if (typeof list.__nsGlideTo === 'function') {
      list.__nsGlideTo(ideal, true);
    } else {
      /* The fallback writes directly, so it needs the same marking as the
         easing does — see gestureLive. */
      list.__nsInternal = (list.__nsInternal || 0) + 1;
      try {
        list.scrollLeft = ideal;
      } finally {
        list.__nsInternal--;
      }
    }
  }

  /* Centre it as the strip's OWN move, so the gesture lock lets it through.
     A thumbnail tapped moments after a swipe is still inside the lock's
     window, and without this the tap would be silently refused — which is
     exactly the "i have to tab twice" the owner hit. */
  function forceCentre(list, slide) {
    list.__nsInternal = (list.__nsInternal || 0) + 1;
    try {
      centreSlide(list, slide);
    } finally {
      list.__nsInternal--;
    }
  }

  /* A THUMBNAIL TAP IS A REQUEST, NOT A SIDE EFFECT (owner, 21 Sep 2026:
     "if im scrolling a long image and i click a thumbnail then i dont go
     directly to that image but i have to tab twice", and "now the thumbnail
     doesnt put me exactly in the center").

     Centring used to be driven only by watching `is-active`, which Dawn sets
     for two completely different reasons: the shopper picked something, or a
     photo drifted past the middle while they were scrolling. The observer
     cannot tell those apart, so it refuses anything that arrives while the
     strip is moving — correctly, because centring during a flick kills it.
     But a tap right after a scroll looks identical to it, so the first tap was
     thrown away and only the second, by which time the strip had gone quiet,
     did anything.

     A click is unambiguous. It is a request, it is always honoured, and it
     goes through `forceCentre` because the shopper's finger may still be
     inside the lock's window from the swipe a moment earlier.

     WHY SEVERAL PASSES. Dawn's own setActiveMedia scrolls the strip to the
     photo's LEFT EDGE from a setTimeout, so a single centring runs first and
     is then undone — which is the "doesn't put me exactly in the center". The
     passes straddle it: one now, one after Dawn's timer, one once the layout
     has settled. `cancelCentre` still calls them all off the moment a finger
     lands, so they can never fight a shopper who has started scrolling again.

     Bound in the capture phase on `document`: Dawn replaces the gallery markup
     on a variant change, so a listener on the thumbnails themselves would not
     survive it. */
  function bindThumbCentring() {
    if (document.__nsThumbCentre) return;
    document.__nsThumbCentre = true;
    document.addEventListener(
      'click',
      function (e) {
        var item = e.target && e.target.closest ? e.target.closest('.thumbnail-list [data-target]') : null;
        if (!item) return;
        var gal = item.closest('media-gallery');
        if (!gal) return;
        var list = gal.querySelector('.ns-gallery-strip');
        if (!list || !isStrip(list)) return;
        var slide = list.querySelector('[data-media-id="' + item.dataset.target + '"]');
        if (!slide) return;

        cancelCentre(list);
        var go = function () {
          forceCentre(list, slide);
        };
        go();
        list.__nsCentreTimers = [
          window.setTimeout(go, 0),
          window.setTimeout(go, 80),
          window.setTimeout(function () {
            list.__nsCentreTimers = null;
            go();
          }, 220),
        ];
      },
      true
    );
  }

  /* A COLOUR TAP IS A REQUEST TOO (owner, 26 Sep 2026: "if i select a variant
     colour but then i scroll through the images and i click the variant again
     to go back to the variant the selector doesnt work", and "sometimes after i
     have been scrolling its hard to get the selector to work at all").

     Two failures, one cause. The strip follows a colour only through the
     is-active watcher in bind(), and that watcher has to ignore a mark that
     arrives within 900ms of a finger or 400ms of a scroll event, so that a
     flick is never cut short:
     - the colour that is ALREADY chosen, tapped again, changes nothing: a
       checked radio fires no change event, so Dawn never marks its photo and
       the strip stays where the shopper scrolled to;
     - a NEW colour tapped just after a swipe is chosen by Dawn, but its photo
       is marked while the strip is still coasting, so the watcher reads it as
       scrolling and drops it: the colour changes, the photo does not, and it
       reads as a dead selector.

     A tap on a variant option is as unambiguous as a thumbnail tap. It is
     noted on the strip (__nsAskAt), and the watcher honours the next mark
     within 3s through forceCentre - unless a finger has gone back onto the
     photos since, because then the shopper has taken over again. The chosen
     colour tapped again is marked here, from the variant Dawn keeps in
     variant-selects. Capture phase on document, like the thumbnails: Dawn
     replaces the picker markup on every variant change. */
  function askedVariantMedia(info) {
    var node = info && info.querySelector('variant-selects [data-selected-variant]');
    if (!node) return null;
    try {
      var v = JSON.parse(node.textContent);
      return v && v.featured_media ? v.featured_media.id : null;
    } catch (err) {
      return null;
    }
  }

  function bindVariantAsk() {
    if (document.__nsVariantAsk) return;
    document.__nsVariantAsk = true;
    document.addEventListener(
      'click',
      function (e) {
        var label = e.target && e.target.closest ? e.target.closest('variant-selects label[for]') : null;
        if (!label) return;
        var input = document.getElementById(label.htmlFor);
        if (!input || input.type !== 'radio') return;
        var info = label.closest('product-info');
        var gal = info && info.querySelector('media-gallery');
        var list = gal && gal.querySelector('.ns-gallery-strip');
        if (!list || !isStrip(list)) return;
        var now = Date.now();
        /* A SECOND TAP WHILE THE FIRST COLOUR IS STILL LOADING (audit, 2 Oct 2026;
           owner: "yes fix it"). The radio is already checked, but the picker still
           describes the OLD colour until Dawn's answer arrives, so the re-tap below
           brought back the old colour's photo and used up the ask the new one
           needed: the gallery stayed on the old colour. Dawn holds
           pendingRequestUrl while that request runs; 8 s caps it, in case a failed
           request never clears it. The tap still renews the ask, so the new
           colour's photo is centred when it arrives, even on a slow line. */
        var loading = input.checked && info.pendingRequestUrl && now - (list.__nsAskAt || 0) < 8000;
        list.__nsAskAt = now;
        /* A new colour: Dawn marks its photo once the variant has loaded, and
           the watcher centres it. Only the colour already chosen is ours. */
        if (!input.checked || loading) return;
        /* Colours only (audit E9, 2 Oct 2026): the size already chosen, tapped
           again, pulled the photos back to the colour's photo. */
        if (!label.matches('.ns-opt-swatch, .swatch-input__label')) return;
        var mediaId = askedVariantMedia(info);
        if (!mediaId || !info.dataset.section || typeof gal.setActiveMedia !== 'function') return;
        var key = info.dataset.section + '-' + mediaId;
        if (!list.querySelector('[data-media-id="' + key + '"]')) return;
        gal.setActiveMedia(key, false);
      },
      true
    );
  }

  function stepTo(list, dir) {
    var slides = slidesOf(list);
    if (!slides.length) return;
    var i = currentIndex(list, slides) + dir;
    if (i < 0) i = 0;
    if (i > slides.length - 1) i = slides.length - 1;
    list.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
  }

  function bindButtons(comp, list) {
    var box = comp.querySelector('.slider-buttons');
    if (!box || box.dataset.nsStrip === 'ready') return;
    box.dataset.nsStrip = 'ready';
    box.addEventListener(
      'click',
      function (e) {
        /* Every width now, not just phones: the arrows step by Dawn's fixed
           pitch wherever the strip is, and the strip has no fixed pitch. */
        if (!isStrip(list)) return;
        var btn = e.target.closest ? e.target.closest('button[name]') : null;
        if (!btn) return;
        /* Dawn's arrow steps by that same fixed pitch and would land
           mid-photo. Stopping the event in the CAPTURE phase keeps its
           handler — bound to the button itself — from ever running. */
        e.preventDefault();
        e.stopPropagation();
        stepTo(list, btn.name === 'next' ? 1 : -1);
      },
      true
    );
  }

  function bind(list) {
    var comp = list.closest('slider-component');
    if (comp) bindButtons(comp, list);
    /* DAWN'S SLIDER PASS IS SKIPPED ON THE STRIP (audit B5/E7, 2 Oct 2026; the
       NORTHSHEEP hook in global.js SliderComponent.update). It ran on every
       scroll event: a counter write and two slide-visibility reads, a forced
       layout per frame (1.7 s over 8 frames at 4x CPU), and the write woke the
       section watcher below while a finger was down. Its counter and arrows
       are hidden on the strip at every width, and this file keeps its own. */
    if (comp && !comp.__nsStripOwned) {
      comp.__nsStripOwned = function () {
        return isStrip(comp);
      };
    }

    /* BLOCK DAWN'S slideChanged BEFORE IT ARRIVES.

       Patching MediaGallery.prototype.onSlideChanged is not enough, and the
       reason is the trap this theme already documents for the slider's scroll
       listener: media-gallery.js binds its handler in the CONSTRUCTOR as
       `this.onSlideChanged.bind(this)`, which captures the function value at
       that moment. The element is upgraded before this file can patch the
       prototype, so the bound listener keeps calling the ORIGINAL for the life
       of the page — measured live: `__nsGuarded: true`, my lookup returning
       thumbnail 3, and thumbnail 4 lit anyway.

       A listener cannot be pre-empted on its own element either: on the target
       itself, capture and bubble listeners run in registration order, and
       Dawn's was registered first. But the capture phase still runs down the
       ANCESTORS first, even for a non-bubbling event — so stopping it here, on
       <media-gallery>, means it never reaches the viewer's listener at all.

       Safe to block outright: on a phone strip this file sets the thumbnail
       itself, and Dawn's index comes from a slide pitch that does not exist
       when every photo is a different width. The prototype patch stays for a
       gallery constructed LATER (a variant swap builds a new element, whose
       constructor then binds the patched method). */
    var gal = list.closest('media-gallery');
    if (gal && !gal.__nsBlockedSlideChanged) {
      gal.__nsBlockedSlideChanged = true;
      gal.addEventListener(
        'slideChanged',
        function (e) {
          if (!isStrip(gal)) return;
          e.stopPropagation();
        },
        true
      );
    }

    if (list.dataset.nsStrip !== 'ready') {
      list.dataset.nsStrip = 'ready';
      var frame = 0;
      var timer = 0;
      /* Restarted on every scroll; fires once the strip has been still. */
      var settleTimer = 0;
      /* Where the strip was when the wait began, so the check below can tell
         whether it is still travelling. See checkSettle. */
      var settleFrom = 0;
      /* A frame is the right moment to read layout, and on a real phone it is
         the only one that ever runs. But requestAnimationFrame stops entirely
         when a tab is not being painted — measured here on a hidden pane:
         rAF never fired, setTimeout did — and the bar would then sit frozen
         on a position the shopper has already scrolled past when they come
         back. Whichever arrives first wins and cancels the other. */
      /* A window's worth of travel that still counts as standing still.
         Two pixels rather than zero: a finished glide can rest a fraction of
         a pixel off and never report an exact match. */
      var STILL_PX = 2;
      var checkSettle = function () {
        settleTimer = 0;
        if (Math.abs(list.scrollLeft - settleFrom) > STILL_PX) {
          settleFrom = list.scrollLeft;
          settleTimer = setTimeout(checkSettle, 120);
          return;
        }
        settle(list);
      };
      var run = function () {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
        if (timer) {
          clearTimeout(timer);
          timer = 0;
        }
        /* __nsScrollAt is stamped by the scroll LISTENER, not here. It used to
           be stamped at this point too, and the leftover made the gate below
           self-defeating: run() marked the strip as having just moved and then
           asked whether it had been still for 200ms, so the answer was always
           no, it re-armed forever, and the settle was never reached at all.
           That is "the settle is not working yet on mobile". */

        /* DURING A TOUCH GESTURE THIS FILE DOES NOTHING AT ALL.

           Owner, 21 Sep 2026, after the scroll-position lock still did not
           fix it: "it still doesnt work". That lock proved the interference
           is not a WRITE — nothing could write by then — so what is left is
           the WORK.

           sync() reads offsetLeft and offsetWidth for every photo to find the
           one under the middle of the screen, then updates the counter and
           lights a thumbnail. That is a forced layout read plus a style
           invalidation, once per frame, on the main thread, while WebKit is
           trying to carry a fling on the scrolling thread. With the scripts
           off — `?native=1` — the flick is fluent; with them on it is not,
           and this is the only work left between the two.

           So during the gesture the strip is left entirely alone, exactly as
           it is in native mode, and the counter and thumbnail are brought up
           to date the moment it is over. Nobody can read a counter during a
           hard flick anyway: it is the position you land on that matters, and
           that is the one now shown.

           The re-check is a timer rather than the next scroll event, because
           the last frame of a fling is followed by no event at all — the
           update would then wait for the shopper to touch the strip again. */
        /* WAITS FOR THE STRIP TO BE STILL, NOT FOR A FIXED TIME.

           Owner, 21 Sep 2026: "now it kinda works but it doesnt stop
           gradually it just does a long scroll and then when it getting slow
           it just stops instead of gradually stopping".

           That was a fixed 900ms window. A hard flick coasts for longer, so
           the file woke up in the middle of one, read six photo positions,
           and cut the tail off — the fling was fine right up to the moment the
           silence expired, which is exactly what an abrupt stop late in a long
           scroll looks like.

           A scroll EVENT is the honest signal, and it costs nothing to read:
           the listener stamps the time without touching layout. While events
           are still arriving the strip is still moving, whoever started it,
           so the work waits. 200ms after the last one the fling has genuinely
           run out and the counter can catch up. */
        if (list.__nsTouching || Date.now() - (list.__nsScrollAt || 0) < 200) {
          if (list.__nsAfter) clearTimeout(list.__nsAfter);
          list.__nsAfter = setTimeout(run, 150);
          return;
        }
        if (list.__nsAfter) {
          clearTimeout(list.__nsAfter);
          list.__nsAfter = 0;
        }

        wakeImages(list);
        sync(list);
        /* Restarted on every scroll, so it fires once the strip goes quiet.

           120ms, not 400 (owner: "as soon as i stop scrolling it already
           should directly move me to frame" — the earlier delay was mistaken
           for the settle being slow, when it was the WAIT that was slow). It
           is long enough not to fire between two notches of a continuous
           spin, and short enough that letting go of the wheel and the photo
           starting to square up feel like the same moment. The movement
           itself is unhurried — see the tween's duration. */
        /* STILL MOVING IS NOT STOPPED (owner, 21 Sep 2026: "the settle
           right now is messing up the free scroll and its pulling too hard
           during the scroll ... only settle if the scrolling is slowing
           down").

           The wait used to treat 120ms of no scroll EVENT as a stop. During
           the long tail of a flick the strip is still gliding but the events
           can fall further apart than that, so the settle started against a
           moving strip and hauled on it — which is the pull. Worse, the
           browser's momentum and the settle's own tween were then both
           writing scrollLeft, the same fight already found once between the
           settle and the wheel.

           So the wait measures the strip instead of listening for silence:
           how far did it travel during the window? More than a couple of
           pixels means momentum is still running, so it re-arms and looks
           again. Nothing is settled until the glide has actually run out,
           and because it keeps checking, the settle still arrives the moment
           it does — the 120ms stays short and immediate on a real stop. */
        if (settleTimer) clearTimeout(settleTimer);
        settleFrom = list.scrollLeft;
        settleTimer = setTimeout(checkSettle, 120);
      };
      list.addEventListener(
        'scroll',
        function () {
          /* Stamped HERE, not inside run(): run is throttled, so a timestamp
             taken there records when the file last looked rather than when
             the strip last moved — and "has it stopped?" then answers itself
             wrongly at exactly the moment a long fling is still going. This
             costs one Date.now() and touches no layout. */
          list.__nsScrollAt = Date.now();
          if (frame || timer) return;
          frame = requestAnimationFrame(run);
          timer = setTimeout(run, 120);
        },
        { passive: true }
      );

      /* Is a finger on the strip right now? Read by settle(), which must not
         move the photos while the shopper is still holding them. Kept here
         rather than in ns-gallery-swipe.js on purpose: that engine can be
         switched off in the section settings, and the browser's own scrolling
         needs the same guard. */
      var touchDown = function () {
        list.__nsTouching = true;
        list.__nsTouchAt = Date.now();
      };
      var touchUp = function () {
        list.__nsTouching = false;
        list.__nsTouchAt = Date.now();
      };
      /* WHILE THE SHOPPER IS SCROLLING, THE STRIP IS THEIRS.

         Owner, 21 Sep 2026: "if i flick then it just lands me on the next
         thumbnail and completely ignoring the scrolling behaviour on top",
         "i can visually see that its snapping on the thumbnails".

         media-gallery.js, in setActiveMedia:

             window.setTimeout(() => {
               if (!this.mql.matches || this.elements.thumbnails) {
                 activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
               }
             });

         `activeMedia.parentElement` IS this strip and `offsetLeft` is a
         photo's left edge, so that line hard-lands the gallery on a photo
         boundary. It is Dawn's, it runs from a setTimeout — so it arrives a
         beat after the finger has gone — and it is reached whenever anything
         marks a new media active, which scrolling does.

         Patching setActiveMedia is not an option: media-gallery.js binds it in
         the CONSTRUCTOR (`this.setActiveMedia.bind(this, …)`), so the element
         keeps calling the original for the life of the page — the same trap
         this file already documents for slideChanged. The scroll itself is
         therefore refused at the door instead.

         Only while the gesture is live. Once the strip has been still for a
         moment the arrows, a variant swap and a thumbnail tap all scroll it
         exactly as before — none of which is something the shopper is fighting
         with at the time. */
      if (!list.__nsOwnScroll) {
        list.__nsOwnScroll = true;

        /* Keyed to the FINGER, not to scroll activity. A fling keeps firing
           scroll events, so a scroll-activity test is true for as long as the
           strip is moving and false the instant it is not — which is precisely
           when Dawn's setTimeout lands. The gesture is what has to be
           protected, and it is not over when the finger lifts: the throw is
           still in the air. 900ms covers it. */
        var gestureLive = function () {
          if (!isStrip(list)) return false;
          /* The settle's own easing raises this while it runs. It is not an
             exemption from the rule — the settle has already proved the strip
             is still, by waiting for the scroll events to stop — it is what
             stops the lock from silently eating the one write that is safe.
             Without it a short drag, where the strip halts the moment the
             finger lifts, would be inside the 900ms window and the photo would
             never square up: the feature would look broken rather than
             blocked, which is the worst way for a guard to fail. */
          /* The bar under the photos, dragged on purpose (audit E8, 2 Oct
             2026; set by ns-scroll-row.js): it did nothing for up to 0.9 s
             after a swipe. */
          if (list.__nsInternal || list.__nsBarDrag) return false;
          return list.__nsTouching || Date.now() - (list.__nsTouchAt || 0) < 900;
        };

        /* Every way in that this theme's code and Dawn's actually use.
           scrollLeft is deliberately NOT guarded: it is the one this file
           writes itself, for the settle and the glide, and blocking it would
           switch those off instead. */
        var nativeScrollTo = list.scrollTo.bind(list);
        list.scrollTo = function (opts) {
          if (gestureLive()) return;
          return nativeScrollTo(opts);
        };

        var nativeScrollBy = list.scrollBy.bind(list);
        list.scrollBy = function (opts) {
          if (gestureLive()) return;
          return nativeScrollBy(opts);
        };

        /* AND THE PROPERTY ITSELF, which is the only guard that can be
           trusted.

           There are nine places in this file alone that write a scroll
           position — the settle, the wheel glide, centreSlide and its
           follow-up passes, the arrows — plus Dawn's and the scrub bar's.
           Every one of them has a good reason and a guard of its own, and
           today went the way it did because guarding them one at a time means
           the next one found is always the one that was missed. The owner,
           having watched this happen repeatedly: "why dont we start from
           scretch and you just use the native sideway scroll".

           This is that, enforced rather than intended. While a finger is on
           this strip — and for the 900ms afterwards in which the throw is
           still travelling — the scroll position cannot be written by
           ANYTHING, including code in this file, including code written
           later that has never heard of the rule.

           It costs the desktop nothing: __nsTouchAt is only ever set by a
           real touch on this strip, so with a mouse the setter is the plain
           one and the wheel glide, the settle and the centring all behave
           exactly as they did. */
        var native = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollLeft');
        if (native && native.set && native.get) {
          Object.defineProperty(list, 'scrollLeft', {
            configurable: true,
            get: function () {
              return native.get.call(this);
            },
            set: function (v) {
              if (gestureLive()) return;
              native.set.call(this, v);
            },
          });
        }

        /* ns-scroll-row.js pages the row by `clientWidth * 0.9`. At the
           owner's viewport that is ~314px — the distance two of the earliest
           measured flicks travelled before stopping, to the pixel. */
        var kids = list.children;
        for (var k = 0; k < kids.length; k++) {
          (function (kid) {
            var nativeInto = kid.scrollIntoView.bind(kid);
            kid.scrollIntoView = function (o) {
              if (gestureLive()) return;
              return nativeInto(o);
            };
          })(kids[k]);
        }
      }

      list.addEventListener('touchstart', touchDown, { passive: true });
      list.addEventListener('touchmove', touchDown, { passive: true });
      list.addEventListener('touchend', touchUp, { passive: true });
      list.addEventListener('touchcancel', touchUp, { passive: true });
    }

    /* THE WHEEL MOVES THE STRIP SIDEWAYS (owner, 2026-09-16: "inside the
       filmstrip i want to be able to scroll sideways to navigate through the
       images").

       A mouse has no sideways wheel, and a strip that can only be moved by its
       bar or its thumbnails feels stuck under a cursor that is already resting
       on it. A trackpad's sideways swipe already works — the browser does that
       itself — so this is really for the wheel.

       IT MUST NOT TRAP THE PAGE. The gallery is ~750px tall on a desktop, so a
       reader scrolling down with the cursor over the photograph would be held
       there forever if every wheel tick were swallowed. At either end of the
       strip the event is left alone and the PAGE scrolls as normal: you can
       always keep going. Only the middle of the row is taken.

       Not passive, because taking the gesture requires preventDefault. */
    if (list.dataset.nsWheel !== 'ready') {
      list.dataset.nsWheel = 'ready';

      /* SMOOTHING (owner, 2026-09-16: "can you make the scrolling more smooth?
         it feels jumpy").

         A mouse wheel arrives in lumps — 100 to 120px per notch — and writing
         each lump straight to scrollLeft makes the strip hop from one position
         to the next. So a notch sets a TARGET and a frame loop eases towards
         it. Successive notches ADD to the same target rather than restarting
         it, which is what keeps a fast spin fluid instead of staccato.

         A trackpad is the opposite case: it already sends a stream of small,
         smooth deltas, and easing those would add lag to something that is
         already fluent. Anything under 40px is applied directly. */
      var goal = null;
      var glide = 0;
      /* The last position the glide itself wrote. If the strip is somewhere
         else by the next notch, something other than the glide moved it. */
      var mine = null;

      /* A SECOND KIND OF MOTION, for settling (owner: "make the settle a bit
         more flowly and fluent ... it still has the jump feeling too much").

         The wheel's easing is exponential decay: it covers a fifth of the
         remaining distance every frame, so it starts FAST and trails off. That
         is right for following a wheel — the strip leaps away under the
         gesture — and wrong for coming to rest, where the fast start is the
         "jump". A settle instead runs for a set time on an ease-in-out curve:
         gentle at both ends, with the speed in the middle where it is not
         noticed. */
      var tween = 0;
      var tFrom = 0;
      var tTo = 0;
      var tStart = 0;
      var tDur = 0;

      var stopGlide = function () {
        if (glide) cancelAnimationFrame(glide);
        if (tween) cancelAnimationFrame(tween);
        glide = 0;
        tween = 0;
        goal = null;
        mine = null;
      };

      /* Slow in, slow out. */
      var easeInOut = function (t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      var tweenStep = function (now) {
        var p = tDur > 0 ? (now - tStart) / tDur : 1;
        if (p < 0) p = 0;
        if (p > 1) p = 1;
        /* Marked as the settle's own write for the duration of the frame, so
           the gesture lock lets it through and goes straight back to refusing
           everything else. Cleared in a finally: an exception here must not
           leave the strip permanently writable. */
        list.__nsInternal = (list.__nsInternal || 0) + 1;
        try {
          list.scrollLeft = tFrom + (tTo - tFrom) * easeInOut(p);
        } finally {
          list.__nsInternal--;
        }
        mine = list.scrollLeft;
        if (p < 1) {
          tween = requestAnimationFrame(tweenStep);
          return;
        }
        tween = 0;
      };
      /* Published on the element so anything that moves the strip on purpose
         — a variant change centring its photo — can call off a glide that
         would otherwise ease the picture straight back off screen. */
      list.__nsStopGlide = stopGlide;
      /* And the other direction: the settle below borrows the same easing, so
         landing a photo in frame uses the motion the wheel already uses. */
      list.__nsGlideTo = function (target, flowing) {
        var limit = list.scrollWidth - list.clientWidth;
        if (target < 0) target = 0;
        if (target > limit) target = limit;

        if (flowing) {
          /* Cancel the wheel's decay first — two loops writing scrollLeft in
             the same frame is a stutter, not a settle. */
          if (glide) cancelAnimationFrame(glide);
          glide = 0;
          goal = null;
          if (tween) cancelAnimationFrame(tween);
          tFrom = list.scrollLeft;
          tTo = target;
          var dist = Math.abs(tTo - tFrom);
          /* UNHURRIED ON PURPOSE (owner: "the movement of reframing itself
             should be slower and smoother"). The reaction is quick — 120ms
             after the wheel goes quiet — and the travel is the slow part,
             which is the opposite way round from before. Roughly 6ms per
             pixel, between 0.45s and 0.85s; a notch's worth of correction
             therefore takes about half a second to glide home. */
          tDur = Math.min(850, Math.max(450, dist * 6));
          tStart = (window.performance && performance.now ? performance.now() : Date.now());
          tween = requestAnimationFrame(tweenStep);
          return;
        }

        goal = target;
        mine = list.scrollLeft;
        if (!glide) glide = requestAnimationFrame(step);
      };

      /* A pending target goes stale the moment anything else moves the strip —
         a thumbnail click, the bar, Dawn scrolling to a variant's photo. Left
         standing, the next notch measures from a place the strip is no longer
         going: it read 520 while the strip sat at the very end, decided it was
         mid-row and swallowed the wheel, so the page would not scroll on. That
         is the scroll trap the end-release exists to prevent. */
      var dropIfMoved = function () {
        if (goal === null || mine === null) return;
        if (Math.abs(list.scrollLeft - mine) > 2) stopGlide();
      };

      var step = function () {
        if (goal === null) {
          glide = 0;
          return;
        }
        var cur = list.scrollLeft;
        var diff = goal - cur;
        /* Close enough that another frame would only chase a rounding error. */
        if (Math.abs(diff) < 0.6) {
          list.scrollLeft = goal;
          mine = list.scrollLeft;
          glide = 0;
          goal = null;
          return;
        }
        list.scrollLeft = cur + diff * 0.2;
        mine = list.scrollLeft;
        /* WHOLE PIXELS (audit, 2 Oct 2026). The browser rounds the position, so
           within about 2.5px a 20% step rounds back to where it was, the gap
           never drops under 0.6px, and this ran every frame for good on an idle
           page (846 frames 3.5 s after one notch). A step that did not move ends
           it, on the goal. */
        if (Math.abs(mine - cur) < 0.5) {
          list.scrollLeft = goal;
          mine = list.scrollLeft;
          glide = 0;
          goal = null;
          return;
        }
        glide = requestAnimationFrame(step);
      };

      /* Anything that takes hold of the strip — the bar, a finger, a
         thumbnail — wins immediately. Two things writing scrollLeft in the
         same frame is exactly the stutter this exists to remove. */
      var host = list.closest('media-gallery') || list;
      host.addEventListener(
        'pointerdown',
        function () {
          stopGlide();
          cancelCentre(list);
        },
        true
      );

      list.addEventListener(
        'wheel',
        function (e) {
          if (e.ctrlKey) return; // a pinch-zoom gesture, not a scroll
          if (!window.matchMedia || !window.matchMedia('(min-width: 750px)').matches) return;
          /* Before the early returns below: a wheel over the gallery is intent
             to browse even when this particular notch is handed to the page.
             That intent also outranks any centring still queued from a colour
             the shopper picked a moment ago. */
          wakeImages(list);
          cancelCentre(list);
          /* THE ZOOM GOES NOW, not when the strip has already moved.

             ns-magnifier drops the zoom on a scroll event — which only arrives
             once this notch has started the strip moving, so the zoom-out and
             the travel happen one after the other and the first notch feels
             spent on the zoom. Switching it off here puts both in the same
             frame: the photo un-zooms as it starts to travel. */
          if (typeof window.__nsZoomOff === 'function') window.__nsZoomOff();
          /* Stamped on every notch, including the ones handed back to the
             page at the ends: the settle reads it to tell a pause between
             notches from a genuine stop. */
          list.__nsWheelAt = Date.now();
          var max = list.scrollWidth - list.clientWidth;
          if (max <= 1) return;
          /* A sideways wheel wins outright; otherwise the vertical one is
             borrowed, which is the whole point on a mouse. */
          var raw = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          if (!raw) return;
          /* deltaMode 1 is lines, 2 is pages — a mouse in Firefox reports
             lines, and 3 of them is not 3 pixels. */
          var unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? list.clientWidth : 1;
          var delta = raw * unit;
          /* A SETTLE IN PROGRESS LETS GO IMMEDIATELY.

             The settle eases on its own loop, and nothing here stopped it: a
             notch turned mid-settle started the wheel's loop as well, and both
             wrote scrollLeft on every frame, each pulling towards a different
             place. That is the "blocks me from scrolling sometimes jumping
             back then fighting against my scroll" — not one grabby rule but
             two animations arguing over the same number. The hand wins. */
          if (tween) {
            cancelAnimationFrame(tween);
            tween = 0;
          }

          /* Throw away a target the strip is no longer travelling towards
             before trusting it — see dropIfMoved(). */
          dropIfMoved();
          /* Measured from where the strip is GOING, not where it is, so a
             second notch mid-glide adds a full notch of travel. */
          var at = goal === null ? list.scrollLeft : goal;
          if ((delta < 0 && at <= 0) || (delta > 0 && at >= max - 1)) {
            /* At the end: hand the gesture back so the page scrolls on. */
            stopGlide();
            return;
          }
          e.preventDefault();
          var next = at + delta;
          if (next < 0) next = 0;
          if (next > max) next = max;

          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (Math.abs(delta) < 40 || reduce) {
            stopGlide();
            list.scrollLeft = next;
            /* Recorded so a following mouse notch does not mistake this for
               someone else having moved the strip. */
            mine = list.scrollLeft;
            return;
          }
          goal = next;
          /* Recorded HERE, not only inside step(). The guard compares the
             strip's live position against the last position this glide was
             responsible for, and if it only ever learned that inside an
             animation frame, then a notch whose frame had not run yet left it
             null — the guard bailed, the stale target survived, and the wheel
             kept being swallowed at the end of the strip. Setting it at the
             moment the target is set makes the guard true from the first
             notch, frames or no frames. */
          mine = list.scrollLeft;
          if (!glide) glide = requestAnimationFrame(step);
        },
        { passive: false }
      );
    }

    /* A VARIANT CHANGE CENTRES ITS PHOTO.

       Dawn marks the chosen photo with `is-active` and then scrolls it to the
       left edge from inside a setTimeout. Watching for that class lets the
       strip centre it instead.

       Done three times on purpose: once now, once after the current task, and
       once after a beat. A MutationObserver callback is a microtask, so it
       runs BEFORE Dawn's setTimeout — centre only once and Dawn's left-align
       lands afterwards and wins. The later passes also catch the layout
       settling when a variant swap changes which photos are rendered. */
    if (!list.__nsActiveWatch && window.MutationObserver) {
      list.__nsActiveWatch = new MutationObserver(function (records) {
        var slide = null;
        /* ONLY A PHOTO THAT HAS JUST BECOME THE SELECTED ONE (audit, 2 Oct 2026).
           Any class written on the selected photo used to count: the zoom wrote
           one on every mouse move, so resting the cursor on a half-hidden
           selected photo snapped the strip to it, and the first wheel notch
           over the zoomed first photo was cancelled by a re-centre (the owner's
           16 Sep "first scroll is zooming out"). oldValue tells a real change
           from a rewrite; Dawn's remove-then-add for a re-picked colour still
           counts, because its add arrives with is-active absent before it. */
        for (var i = 0; i < records.length; i++) {
          var t = records[i].target;
          if (
            t &&
            t.parentNode === list &&
            t.classList &&
            t.classList.contains('is-active') &&
            !/(^|\s)is-active(\s|$)/.test(records[i].oldValue || '')
          ) slide = t;
        }
        if (!slide) return;
        /* IS-ACTIVE ALSO MOVES WHEN THE SHOPPER SCROLLS, and that is not a
           request to centre anything (owner, 21 Sep 2026: "i want to give it
           a harder swipe to look through images but then as soon as i let
           loose it stops scrolling").

           This observer exists for variant picks: choose a colour and its
           photo comes to the middle. But the active slide also changes as
           each photo passes the middle during a swipe, and centreSlide writes
           scrollLeft — which cancels the browser's momentum outright. So a
           flick died on the first photo it passed, every time, and it read as
           the strip refusing to scroll.

           cancelCentre() below was not enough: it calls off the follow-up
           passes, while the immediate centreSlide still ran.

           A finger on the glass, or any movement in the last 400ms, means
           the change came from scrolling. Tapping a swatch leaves the strip
           quiet, so a real variant pick still centres. */
        /* The 400ms window closed exactly when a fling's own scroll events
           stopped arriving — which is the moment the throw is still in the
           air and most in need of being left alone. The finger is the honest
           signal, and 900ms covers the tail of a hard flick. */
        /* ...UNLESS THE SHOPPER ASKED (26 Sep 2026). A colour tap in the last
           3s (bindVariantAsk) makes this mark a request, even while the strip
           still coasts from an earlier swipe; forceCentre gets through the
           gesture lock. A finger back on the photos since the tap cancels it. */
        var askAt = list.__nsAskAt || 0;
        var asked =
          askAt > 0 &&
          Date.now() - askAt < 3000 &&
          !list.__nsTouching &&
          (list.__nsTouchAt || 0) <= askAt;
        if (
          !asked &&
          (list.__nsTouching ||
            Date.now() - (list.__nsTouchAt || 0) < 900 ||
            Date.now() - (list.__nsScrollAt || 0) < 400)
        ) {
          cancelCentre(list);
          return;
        }
        if (asked) {
          list.__nsAskAt = 0;
          cancelCentre(list);
          var put = function () {
            forceCentre(list, slide);
          };
          put();
          list.__nsCentreTimers = [
            window.setTimeout(put, 0),
            window.setTimeout(put, 80),
            window.setTimeout(function () {
              list.__nsCentreTimers = null;
              put();
            }, 220)
          ];
          return;
        }
        /* A colour tapped while the last one was still settling supersedes
           it — otherwise two sets of passes fight over the strip. */
        cancelCentre(list);
        centreSlide(list, slide);
        /* The follow-ups are KEPT so they can be called off: Dawn's own
           left-align lands in a setTimeout after this callback (a
           MutationObserver runs as a microtask), and the later pass catches
           the layout settling when a variant swap changes which photos are
           rendered — but neither is worth overriding a shopper who has
           already started scrolling. */
        list.__nsCentreTimers = [
          window.setTimeout(function () {
            centreSlide(list, slide);
          }, 0),
          window.setTimeout(function () {
            list.__nsCentreTimers = null;
            centreSlide(list, slide);
          }, 80)
        ];
      });
      list.__nsActiveWatch.observe(list, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
        subtree: true
      });
    }

    /* The photos are lazy: at DOMContentLoaded the strip has no width yet, so
       the bar cannot be sized. Draw it again once they have arrived. */
    if (document.readyState !== 'complete') {
      window.addEventListener(
        'load',
        function () {
          sync(list);
        },
        { once: true }
      );
    }

    sync(list);
  }

  /* GUARD DAWN'S OWN SLIDE HANDLER.

     Dawn works out the current slide as `round(scrollLeft / pitch) + 1`, where
     the pitch is the gap between the first two slides (global.js). On this
     strip the slides are different widths, so the sum overshoots: on the
     sheepskin, 2245 / 352 + 1 lands on page 7 of a 5-photo gallery. It then
     dispatches slideChanged carrying `sliderItemsToShow[6]` — undefined — and
     media-gallery.js reads `.dataset` on it and throws.

     Caught as an uncaught TypeError in the console. It breaks nothing visible
     (this file's own listener is separate and still runs), but it fires on
     every deep scroll of every product page, and a console that cries wolf is
     how a real error gets missed later. The event carries no element, so there
     is nothing to do with it: drop it and let ours through. */
  function guardGallery() {
    var ctor = window.customElements && customElements.get('media-gallery');
    var proto = ctor && ctor.prototype;
    if (!proto || proto.__nsGuarded || typeof proto.onSlideChanged !== 'function') return;
    var original = proto.onSlideChanged;
    proto.onSlideChanged = function (event) {
      if (!event || !event.detail || !event.detail.currentElement) return;
      /* On a filmstrip gallery this file sets the thumbnail itself, above.
         Dawn's own event carries an index from a pitch that does not exist
         here, and because the handler is debounced its late arrival would
         simply overwrite the right answer with the wrong one. */
      if (isStrip(this)) return;
      return original.call(this, event);
    };
    proto.__nsGuarded = true;
  }

  /* A variant change re-renders the gallery. Debounced to the house
     convention — one change fires a burst of mutations. */
  var pending = 0;
  var mo = null;

  function refresh() {
    strips().forEach(bind);
    watch();
  }

  function watch() {
    if (!mo) return;
    mo.disconnect();
    var lists = strips();
    var hosts = [];
    for (var i = 0; i < lists.length; i++) {
      var host = lists[i].closest('.shopify-section');
      if (host && hosts.indexOf(host) === -1) hosts.push(host);
    }
    if (!hosts.length) {
      mo.observe(document.body, { childList: true, subtree: true });
      return;
    }
    hosts.forEach(function (h) {
      mo.observe(h, { childList: true, subtree: true });
    });
  }

  function start() {
    guardGallery();
    /* AND AGAIN WHEN THE ELEMENT EXISTS.

       This file is deferred, so by the time it runs `document.readyState` is
       already "interactive" and start() is called immediately — before
       media-gallery.js (also deferred, and loaded after it) has defined the
       element. `customElements.get('media-gallery')` was therefore undefined,
       guardGallery() returned without patching anything, and it never tried
       again: measured on the live page as `__nsGuarded: false`, with Dawn's
       debounced handler still overwriting the right thumbnail with its
       fixed-pitch guess half a second later — photo 3 highlighted thumbnail 4
       (owner: "it still doesnt highlight the correct image ... the thumbnail
       images still think all images are same width").

       whenDefined resolves whenever that definition lands, before or after
       this line; guardGallery() is idempotent, so calling it twice is free. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('media-gallery').then(guardGallery);
    }
    mo = new MutationObserver(function () {
      if (pending) clearTimeout(pending);
      pending = setTimeout(function later() {
        /* Never while a finger is on a strip (audit E7, 2 Oct 2026): a
           re-render seen mid-swipe waits until the throw is over. */
        var busy = strips().some(function (l) {
          return l.__nsTouching || Date.now() - (l.__nsTouchAt || 0) < 900;
        });
        if (busy) {
          pending = setTimeout(later, 200);
          return;
        }
        pending = 0;
        refresh();
      }, 60);
    });
    /* ONE SET OF PAGE LISTENERS (audit E10, 2 Oct 2026). A colour swap runs
       this file again (CLAUDE.md), and every run added another resize and
       section-load listener and left its watcher running: N swaps, N+1 syncs
       per resize. The listeners are added once and call the latest run. */
    if (window.__nsStripMo && window.__nsStripMo !== mo) window.__nsStripMo.disconnect();
    window.__nsStripMo = mo;
    window.__nsStripRefresh = refresh;
    window.__nsStripResync = function () {
      strips().forEach(function (list) {
        list.__nsStripAt = null;
        sync(list);
      });
    };
    refresh();
    bindThumbCentring();
    bindVariantAsk();
    scheduleWarm();
    if (!window.__nsStripPageBound) {
      window.__nsStripPageBound = true;
      window.addEventListener(
        'resize',
        function () {
          window.__nsStripResync();
        },
        { passive: true }
      );
      document.addEventListener('shopify:section:load', function () {
        window.__nsStripRefresh();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
